﻿// Live dashboard/list updates over SignalR, with a self-healing connection and a
// safety-net poll so a wedged or permanently-closed socket can never leave the page
// silently stale.
//
// Background: signalR.withAutomaticReconnect() only retries for ~30s (default policy
// [0, 2s, 10s, 30s]) and then closes for good — at which point updates stop until a
// full page reload. On Azure App Service that happens routinely (app-pool recycle,
// WebSocket idle timeout, laptop sleep). We therefore (a) restart the connection
// ourselves with backoff after a hard close, and (b) refresh every wired container at
// least once every SAFETY_POLL_MS regardless of socket health.

// --- Minimal ambient typings for the CDN-loaded SignalR client (no @types installed) ---
interface SignalRHubConnection {
    start(): Promise<void>;
    on(method: string, cb: (...args: unknown[]) => void): void;
    onreconnecting(cb: (err?: Error) => void): void;
    onreconnected(cb: () => void): void;
    onclose(cb: (err?: Error) => void): void;
    invoke(method: string, ...args: unknown[]): Promise<unknown>;
}
interface SignalRHubConnectionBuilder {
    withUrl(url: string): SignalRHubConnectionBuilder;
    withAutomaticReconnect(): SignalRHubConnectionBuilder;
    build(): SignalRHubConnection;
}
declare const signalR: {
    HubConnectionBuilder: new () => SignalRHubConnectionBuilder;
} | undefined;

type ConnectionState = "connected" | "reconnecting" | "disconnected";

interface ChangePayload {
    scope: string;
    tenantId: number;
    siteId?: number;
}

type GroupJoin =
    | { kind: "site"; scope: string; tenantId: number; siteId: number }
    | { kind: "tenant"; scope: string; tenantId: number };

(function () {
    "use strict";

    if (typeof signalR === "undefined") {
        console.warn("[live-updates] SignalR client not loaded; live updates disabled.");
        return;
    }

    // Refresh every wired container at least this often, even if SignalR delivers
    // nothing. The timer is reset on every successful refresh, so when events are
    // flowing this never fires — it is purely a floor on staleness.
    const SAFETY_POLL_MS = 60000;
    // Backoff schedule (ms) for manual restarts after a hard close. Last value repeats.
    const RESTART_BACKOFF_MS = [2000, 5000, 10000, 30000];

    let connection: SignalRHubConnection | null = null;
    let connectionPromise: Promise<SignalRHubConnection> | null = null;
    let restarting = false;

    // Track group joins so we can re-issue them after a reconnect. SignalR preserves
    // client-side event handlers across reconnects, but server-side group membership is
    // lost — so without rejoining, updates would silently stop. (PRD 0053)
    const groupJoins: GroupJoin[] = [];

    function setConnectionState(state: ConnectionState): void {
        document.dispatchEvent(new CustomEvent("live-updates:connection", { detail: { state } }));
    }

    function rememberJoin(join: GroupJoin): void {
        groupJoins.push(join);
    }

    function applyJoin(conn: SignalRHubConnection, join: GroupJoin): Promise<unknown> {
        if (join.kind === "site") {
            return conn.invoke("JoinSiteGroup", join.scope, join.tenantId, join.siteId).catch(function (err) {
                console.warn("[live-updates] JoinSiteGroup failed", join.scope, join.siteId, err);
            });
        }
        return conn.invoke("JoinTenantGroup", join.scope, join.tenantId).catch(function (err) {
            console.warn("[live-updates] JoinTenantGroup failed", join.scope, err);
        });
    }

    function rejoinAll(conn: SignalRHubConnection): void {
        groupJoins.forEach(function (join) { applyJoin(conn, join); });
    }

    function getConnection(): Promise<SignalRHubConnection> {
        if (connection) return Promise.resolve(connection);
        if (connectionPromise) return connectionPromise;

        const conn = new signalR!.HubConnectionBuilder()
            .withUrl("/hubs/live")
            .withAutomaticReconnect()
            .build();
        connection = conn;

        // Re-issue every recorded group join after an automatic reconnect. Event handlers
        // ('changed') are preserved across reconnects, so we MUST NOT re-register them here
        // — that would cause duplicate refreshes.
        conn.onreconnecting(function () { setConnectionState("reconnecting"); });
        conn.onreconnected(function () {
            setConnectionState("connected");
            rejoinAll(conn);
        });

        // withAutomaticReconnect() gives up after its default schedule (~30s). When it does,
        // onclose fires and the connection is dead for good unless we restart it ourselves.
        conn.onclose(function (err) {
            console.warn("[live-updates] connection closed; will attempt to restart.", err);
            setConnectionState("disconnected");
            restartConnection(conn, 0);
        });

        connectionPromise = conn.start()
            .then(function () {
                setConnectionState("connected");
                return conn;
            })
            .catch(function (err) {
                console.warn("[live-updates] hub connection failed:", err);
                connection = null;
                connectionPromise = null;
                setConnectionState("disconnected");
                // Retry the very first connection too, so a page opened during a blip recovers.
                restartConnection(conn, 0);
                throw err;
            });

        return connectionPromise;
    }

    // Manually restart the SAME connection instance (preserving its 'changed' handlers)
    // with capped backoff, then rejoin all groups. Reuses the existing instance so the
    // per-container listeners registered via getConnection().then() keep working.
    function restartConnection(conn: SignalRHubConnection, attempt: number): void {
        if (restarting) return;
        restarting = true;
        setConnectionState("reconnecting");

        const delay = RESTART_BACKOFF_MS[Math.min(attempt, RESTART_BACKOFF_MS.length - 1)];
        setTimeout(function () {
            conn.start()
                .then(function () {
                    restarting = false;
                    connection = conn;
                    connectionPromise = Promise.resolve(conn);
                    setConnectionState("connected");
                    rejoinAll(conn);
                })
                .catch(function (err) {
                    console.warn("[live-updates] restart attempt " + (attempt + 1) + " failed:", err);
                    restarting = false;
                    restartConnection(conn, attempt + 1);
                });
        }, delay);
    }

    function wireContainer(container: HTMLElement): void {
        if (container.dataset.liveWired === "1") return;
        container.dataset.liveWired = "1";

        const scope = container.dataset.liveScope;
        const tenantId = parseInt(container.dataset.liveTenantId ?? "", 10);
        const siteIdsRaw = container.dataset.liveSiteIds || "";

        if (!scope || !container.dataset.liveUrl) {
            console.warn("[live-updates] container missing required data- attributes", container);
            return;
        }

        // A tenant id is required to JOIN a SignalR group and to match incoming payloads, but NOT to
        // refresh. Callers with no single tenant render data-live-tenant-id="0" (a CatevoAdministrator
        // spans every tenant; an issuer with no organisation has none), and bailing out here used to
        // kill the safety poll and the refresh-now listener along with the socket — leaving those
        // users a permanently static page whose filter changed the URL but never the data.
        const hasTenantScope = !isNaN(tenantId) && tenantId > 0;

        const siteIds = siteIdsRaw
            .split(",")
            .map(function (s) { return parseInt(s.trim(), 10); })
            .filter(function (n) { return !isNaN(n); });

        let refreshTimer: number | null = null;
        function scheduleRefresh(): void {
            if (refreshTimer !== null) return;
            refreshTimer = window.setTimeout(function () {
                refreshTimer = null;
                refresh();
            }, 250);
        }

        // The URL is read from the dataset at refresh time, NOT captured at wire time: pages with
        // filters (e.g. the dashboard, PRD 0178) repoint data-live-url when the filter changes, and
        // every later refresh must pick up the new value rather than the one baked in at page load.
        function refresh(): void {
            const current = container.dataset.liveUrl;
            if (current) refreshContainer(container, current);
        }

        // Safety-net poll: refresh at least every SAFETY_POLL_MS. Reset on every refresh
        // (from any source) so an active SignalR feed keeps it from ever firing, while a
        // wedged or closed socket still gets caught within the interval.
        let pollTimer: number | null = null;
        function armPoll(): void {
            if (pollTimer !== null) window.clearTimeout(pollTimer);
            pollTimer = window.setTimeout(refresh, SAFETY_POLL_MS);
        }
        container.addEventListener("live-updates:refreshed", armPoll);
        armPoll();

        // On-demand refresh for page code that has just changed data-live-url (filter changes).
        container.addEventListener("live-updates:refresh-now", refresh);

        // No tenant scope: the local refresh machinery above is fully wired, so filter changes and the
        // safety poll still work. Only the push feed is skipped — there is no single tenant group to
        // join, and payload.tenantId could never match.
        if (!hasTenantScope) {
            console.info("[live-updates] no tenant scope; using safety poll only", container);
            return;
        }

        getConnection().then(function (conn) {
            conn.on("changed", function (...args: unknown[]) {
                const payload = args[0] as ChangePayload | undefined;
                if (!payload || payload.scope !== scope) return;
                if (payload.tenantId !== tenantId) return;

                if (siteIds.length > 0) {
                    // Site-scoped listener: only refresh when the change is for one of our sites.
                    if (!payload.siteId || siteIds.indexOf(payload.siteId) === -1) return;
                }
                // Tenant-wide listener (siteIds empty) refreshes on every tenant event.

                scheduleRefresh();
            });

            if (siteIds.length > 0) {
                siteIds.forEach(function (sid) {
                    const join: GroupJoin = { kind: "site", scope: scope, tenantId: tenantId, siteId: sid };
                    rememberJoin(join);
                    applyJoin(conn, join);
                });
            } else {
                const join: GroupJoin = { kind: "tenant", scope: scope, tenantId: tenantId };
                rememberJoin(join);
                applyJoin(conn, join);
            }
        }).catch(function () { /* already logged; safety-net poll keeps the page fresh */ });
    }

    function refreshContainer(container: HTMLElement, url: string): void {
        fetch(url, { credentials: "same-origin", headers: { "X-Requested-With": "XMLHttpRequest" } })
            .then(function (res) {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.text();
            })
            .then(function (html) {
                container.innerHTML = html;
                updateCount(container);
                container.dispatchEvent(new CustomEvent("live-updates:refreshed", {
                    bubbles: true,
                    detail: { container: container }
                }));
            })
            .catch(function (err) {
                console.warn("[live-updates] refresh failed for", url, err);
            });
    }

    function updateCount(container: HTMLElement): void {
        const selector = container.dataset.liveCountTarget;
        if (!selector) return;
        const source = container.querySelector("[data-permits-count]");
        const target = document.querySelector(selector);
        if (source && target && source !== target) {
            target.textContent = source.textContent;
        }
    }

    function init(): void {
        document.querySelectorAll<HTMLElement>("[data-live-scope]").forEach(wireContainer);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }
})();
